import React, { useRef, useEffect } from 'react';

interface NumericStepperProps {
    label: string;
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    step?: number;
    preserveDecimal?: boolean; // New prop for weight input behavior
    disableWheel?: boolean; // New prop to disable mouse wheel
    isWarning?: boolean; // Optional visual warning state (e.g. overweight)
    labelPosition?: 'top' | 'inline';
    compact?: boolean;
    tooltip?: string;
    disabled?: boolean;
}

const NumericStepper: React.FC<NumericStepperProps> = ({
    label,
    value,
    onChange,
    min = -Infinity,
    max = Infinity,
    step = 1,
    preserveDecimal = false,
    disableWheel = true,
    isWarning = false,
    labelPosition = 'top',
    compact = false,
    tooltip,
    disabled = false,
}) => {
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleStep = (direction: 'up' | 'down') => {
        if (disabled) return;
        let newValue: number;

        if (preserveDecimal) {
            // Preserve decimal part when stepping
            const decimalPart = value % 1; // Get decimal part (e.g., 0.3 from 2.3)
            const integerPart = Math.floor(value); // Get integer part (e.g., 2 from 2.3)
            const steppedInteger = direction === 'up' ? integerPart + step : integerPart - step;
            newValue = steppedInteger + decimalPart;
            // Round to 1 decimal place to avoid floating point precision issues
            newValue = Math.round(newValue * 10) / 10;
        } else {
            // Normal stepping behavior
            newValue = direction === 'up' ? value + step : value - step;

            // Fix floating point precision issues (e.g. 1.1 + 0.1 = 1.2000000000000002)
            const stepStr = step.toString();
            const decimalPlaces = stepStr.indexOf('.') !== -1 ? stepStr.split('.')[1].length : 0;
            newValue = parseFloat(newValue.toFixed(decimalPlaces));
        }

        if (newValue >= min && newValue <= max) {
            onChange(newValue);
        }
    };

    const startStepping = (direction: 'up' | 'down') => {
        if (disabled) return;
        handleStep(direction);
        timeoutRef.current = setTimeout(() => {
            intervalRef.current = setInterval(() => {
                handleStep(direction);
            }, 100);
        }, 500);
    };

    const stopStepping = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (intervalRef.current) clearInterval(intervalRef.current);
    };

    useEffect(() => {
        // Cleanup on unmount
        return () => stopStepping();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) return;
        const inputValue = e.target.value;

        if (inputValue === '') {
            onChange(min);
            return;
        }

        const numValue = parseFloat(inputValue);

        if (!isNaN(numValue)) {
            // Allow any valid number input, but clamp to min/max on blur or when stepping
            onChange(numValue);
        }
    };

    const handleBlur = () => {
        // Clamp value to min/max bounds when user finishes editing
        if (value < min) {
            onChange(min);
        } else if (value > max) {
            onChange(max);
        }
    };

    useEffect(() => {
        const node = containerRef.current;
        if (!node) return;

        const wheelHandler = (event: WheelEvent) => {
            event.preventDefault();
            if (disableWheel) {
                inputRef.current?.blur();
                return;
            }
            if (event.deltaY < 0) {
                handleStep('up');
            } else {
                handleStep('down');
            }
        };

        node.addEventListener('wheel', wheelHandler, { passive: false });
        return () => {
            node.removeEventListener('wheel', wheelHandler);
        };
    }, [disableWheel, handleStep]);

    const commonInputClasses = "w-full text-center font-mono text-slate-900 dark:text-white focus:outline-none";
    const containerSizeClasses = compact ? 'w-full h-8 text-sm sm:max-w-20 sm:h-6' : 'w-full h-9 sm:max-w-36 sm:h-7';
    const buttonPaddingClasses = compact ? 'px-1.5 py-0.5 sm:px-1' : 'px-2 py-1 sm:px-1';
    const labelClasses = labelPosition === 'inline'
        ? 'text-xs font-semibold text-slate-600 dark:text-white whitespace-nowrap'
        : 'block text-xs font-medium text-slate-700 dark:text-white mb-0.5';

    const inputElement = (
        <div
            ref={containerRef}
            className={`relative flex items-center w-full ${containerSizeClasses} rounded-md border border-slate-300 dark:border-white/10 hover:border-slate-400 dark:hover:border-white/20 bg-white dark:bg-white/10 shadow-sm transition backdrop-blur-md ${isWarning
                ? 'border-red-500 hover:border-red-500 bg-red-50 dark:bg-red-500/10'
                : ''
                } ${disabled ? 'opacity-50 grayscale pointer-events-none cursor-not-allowed bg-slate-100 dark:bg-slate-800/50' : ''}`}
        >
            <button
                type="button"
                onMouseDown={() => startStepping('down')}
                onMouseUp={stopStepping}
                onMouseLeave={stopStepping}
                onTouchStart={(e) => { e.preventDefault(); startStepping('down'); }}
                onTouchEnd={(e) => { e.preventDefault(); stopStepping(); }}
                disabled={disabled}
                className={`${buttonPaddingClasses} min-w-[34px] sm:min-w-[28px] h-full flex items-center justify-center bg-slate-100/60 dark:bg-white/5 hover:bg-orange-100 dark:hover:bg-orange-500/20 text-slate-600 dark:text-slate-200 hover:text-orange-600 dark:hover:text-orange-400 transition-all rounded-l-md focus:outline-none active:scale-95 active:bg-orange-500 active:text-white disabled:opacity-50 disabled:cursor-not-allowed border-r border-slate-200 dark:border-white/10`}
                aria-label="Decrement"
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0"><path d="M6.75 9.25a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5z" /></svg>
            </button>
            <input
                type="number"
                inputMode="decimal"
                ref={inputRef}
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={(e) => e.target.select()}
                min={min}
                max={max}
                disabled={disabled}
                step={preserveDecimal ? "any" : step}
                className={`${commonInputClasses} ${compact ? 'text-sm py-1' : 'text-base py-1'} ${isWarning ? 'bg-red-50 dark:bg-transparent' : 'bg-transparent'
                    } disabled:cursor-not-allowed`}
                aria-label={label}
            />
            <button
                type="button"
                onMouseDown={() => startStepping('up')}
                onMouseUp={stopStepping}
                onMouseLeave={stopStepping}
                onTouchStart={(e) => { e.preventDefault(); startStepping('up'); }}
                onTouchEnd={(e) => { e.preventDefault(); stopStepping(); }}
                disabled={disabled}
                className={`${buttonPaddingClasses} min-w-[34px] sm:min-w-[28px] h-full flex items-center justify-center bg-slate-100/60 dark:bg-white/5 hover:bg-orange-100 dark:hover:bg-orange-500/20 text-slate-600 dark:text-slate-200 hover:text-orange-600 dark:hover:text-orange-400 transition-all rounded-r-md focus:outline-none active:scale-95 active:bg-orange-500 active:text-white disabled:opacity-50 disabled:cursor-not-allowed border-l border-slate-200 dark:border-white/10`}
                aria-label="Increment"
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0"><path d="M10.75 6.75a.75.75 0 00-1.5 0v2.5h-2.5a.75.75 0 000 1.5h2.5v2.5a.75.75 0 001.5 0v-2.5h2.5a.75.75 0 000-1.5h-2.5v-2.5z" /></svg>
            </button>
        </div>
    );

    if (labelPosition === 'inline') {
        return (
            <div className="flex items-center justify-end gap-2">
                <label className={labelClasses}>{label}</label>
                {inputElement}
            </div>
        );
    }

    return (
        <div className="w-full">
            {(label || tooltip) && (
                <div className="flex items-center justify-between gap-1 mb-1 min-h-[18px]">
                    <label className="block text-[11px] sm:text-xs font-medium text-slate-700 dark:text-gray-300 truncate">
                        {label}
                    </label>
                    {tooltip && (
                    <div className="relative shrink-0 hidden sm:block">
                        <button
                            type="button"
                            className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-orange-50 dark:bg-orange-950/30 text-orange-500 hover:bg-orange-100 transition-all border border-orange-200/50 shadow-sm shrink-0"
                            title={tooltip}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                    )}
                </div>
            )}
            {inputElement}
        </div>
    );
};

export default NumericStepper;
